import * as React from "react"
import { motion } from "motion/react"
import {
  Flame,
  Plus,
  X,
  Check,
  Loader2,
  Calendar,
  Gift,
  Shield,
  Clock,
  Target,
} from "lucide-react"
import type { CreateStreakData, Milestone, MilestoneReward } from "@/contexts/AuthContext"

/* ─── Types ─── */

export interface StreakFormData {
  name: string
  type: "daily_login" | "deal_submission" | "referral" | "chat_active" | "custom"
  description: string
  milestones: Milestone[]
  defaultReward: MilestoneReward
  resetOnMiss: boolean
  gracePeriodDays: number
  maxStreak: number | null
  isActive: boolean
  startDate: string
  endDate: string
}

export const emptyForm = (): StreakFormData => ({
  name: "",
  type: "daily_login",
  description: "",
  milestones: [],
  defaultReward: { type: "points", amount: 25 },
  resetOnMiss: true,
  gracePeriodDays: 0,
  maxStreak: null,
  isActive: true,
  startDate: "",
  endDate: "",
})

const STREAK_TYPES = [
  { value: "daily_login", label: "Daily Login" },
  { value: "deal_submission", label: "Deal Submission" },
  { value: "referral", label: "Referral" },
  { value: "chat_active", label: "Chat Active" },
  { value: "custom", label: "Custom" },
] as const

const REWARD_TYPES = [
  { value: "points", label: "Points" },
  { value: "free_spin", label: "Free Spin" },
  { value: "raffle_ticket", label: "Raffle Ticket" },
  { value: "badge", label: "Badge" },
] as const

const inputClass =
  "w-full px-3 py-2.5 rounded-lg bg-[#0C0C12] border border-[#2A2A45] text-sm text-white/85 font-admin placeholder:text-white/15 outline-none focus:border-[#F37020]/50 focus:ring-1 focus:ring-[#F37020]/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed"

const selectClass =
  "w-full px-3 py-2.5 rounded-lg bg-[#0C0C12] border border-[#2A2A45] text-sm text-white/85 font-admin outline-none focus:border-[#F37020]/50 focus:ring-1 focus:ring-[#F37020]/20 transition-all cursor-pointer"

function FormField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <label className="text-xs font-medium text-white/50 font-admin">{label}</label>
      {children}
    </div>
  )
}

interface StreakFormProps {
  form: StreakFormData
  editingId: string | null
  saving: boolean
  onClose: () => void
  onSave: () => void
  updateForm: <K extends keyof StreakFormData>(key: K, value: StreakFormData[K]) => void
}

export function StreakFormModal({ form, editingId, saving, onClose, onSave, updateForm }: StreakFormProps) {
  const addMilestone = () => {
    const nextDay = form.milestones.length > 0 ? Math.max(...form.milestones.map((m) => m.day)) + 1 : 1
    updateForm("milestones", [...form.milestones, { day: nextDay, reward: { type: "points", amount: 50 } }])
  }

  const removeMilestone = (index: number) => {
    updateForm("milestones", form.milestones.filter((_, i) => i !== index))
  }

  const updateMilestone = (index: number, field: "day" | "type" | "amount", value: any) => {
    const updated = form.milestones.map((m, i) => {
      if (i !== index) return m
      if (field === "day") return { ...m, day: value }
      return { ...m, reward: { ...m.reward, [field]: value } }
    })
    updateForm("milestones", updated)
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 24, scale: 0.97 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 24, scale: 0.97 }}
      transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
      className="relative w-full max-w-2xl mx-4 my-8 bg-[#111117] border border-[#2A2A45] rounded-2xl shadow-2xl overflow-hidden"
      onClick={(e) => e.stopPropagation()}
    >
      {/* Header */}
      <div className="flex items-center justify-between gap-4 px-6 py-4 border-b border-[#2A2A45]">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#F37020]/20 to-[#F37020]/5 border border-[#F37020]/20 flex items-center justify-center">
            <Flame className="w-5 h-5 text-[#F37020]/80" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-white/90 font-admin">{editingId ? "Edit Streak" : "Create Streak"}</h3>
            <p className="text-xs text-white/30 font-admin mt-0.5">
              {editingId ? `Editing "${form.name || "Untitled"}"` : "Configure a new streak for your users"}
            </p>
          </div>
        </div>
        <button type="button" onClick={onClose} className="w-8 h-8 rounded-lg bg-white/[0.04] border border-[#2A2A45] inline-flex items-center justify-center text-white/40 hover:text-white/70 hover:bg-white/[0.08] transition-colors">
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Body */}
      <div className="px-6 py-5 max-h-[calc(100vh-12rem)] overflow-y-auto space-y-5">
        {/* Basic Info */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Target className="w-4 h-4 text-white/25" />
            <h4 className="text-xs font-semibold text-white/50 uppercase tracking-wider font-admin">Basic Information</h4>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField label="Streak Name">
              <input value={form.name} onChange={(e) => updateForm("name", e.target.value)} placeholder="Daily Login Streak" className={inputClass} />
            </FormField>
            <FormField label="Type">
              <select value={form.type} onChange={(e) => updateForm("type", e.target.value as any)} className={selectClass}>
                {STREAK_TYPES.map((t) => (<option key={t.value} value={t.value}>{t.label}</option>))}
              </select>
            </FormField>
          </div>
          <div className="mt-4">
            <FormField label="Description">
              <textarea value={form.description} onChange={(e) => updateForm("description", e.target.value)} rows={2} placeholder="Describe what this streak rewards..." className={`${inputClass} resize-y min-h-[60px]`} />
            </FormField>
          </div>
        </div>

        {/* Default Reward */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Gift className="w-4 h-4 text-white/25" />
            <h4 className="text-xs font-semibold text-white/50 uppercase tracking-wider font-admin">Default Reward</h4>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <FormField label="Reward Type">
              <select value={form.defaultReward.type} onChange={(e) => updateForm("defaultReward", { ...form.defaultReward, type: e.target.value as any })} className={selectClass}>
                {REWARD_TYPES.map((t) => (<option key={t.value} value={t.value}>{t.label}</option>))}
              </select>
            </FormField>
            <FormField label="Amount">
              <input type="number" value={form.defaultReward.amount} onChange={(e) => updateForm("defaultReward", { ...form.defaultReward, amount: parseInt(e.target.value) || 0 })} min={0} className={inputClass} />
            </FormField>
          </div>
        </div>

        {/* Milestones */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-white/25" />
              <h4 className="text-xs font-semibold text-white/50 uppercase tracking-wider font-admin">Milestones</h4>
            </div>
            <button type="button" onClick={addMilestone} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#F37020]/10 border border-[#F37020]/20 text-xs text-[#F37020] font-admin hover:bg-[#F37020]/20 transition-colors">
              <Plus className="w-3 h-3" />Add
            </button>
          </div>
          {form.milestones.length === 0 ? (
            <p className="text-xs text-white/25 font-admin py-4 text-center border border-dashed border-[#2A2A45] rounded-lg">No milestones configured.</p>
          ) : (
            <div className="space-y-2">
              {form.milestones.map((milestone, index) => (
                <div key={index} className="flex items-center gap-3 p-3 rounded-lg bg-[#0C0C12] border border-[#2A2A45]">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-white/40 font-admin">Day</span>
                    <input type="number" value={milestone.day} onChange={(e) => updateMilestone(index, "day", parseInt(e.target.value) || 1)} min={1} className="w-16 px-2 py-1.5 rounded bg-[#111117] border border-[#2A2A45] text-sm text-white/85 font-admin text-center" />
                  </div>
                  <select value={milestone.reward.type} onChange={(e) => updateMilestone(index, "type", e.target.value)} className="flex-1 px-2 py-1.5 rounded bg-[#111117] border border-[#2A2A45] text-sm text-white/85 font-admin">
                    {REWARD_TYPES.map((t) => (<option key={t.value} value={t.value}>{t.label}</option>))}
                  </select>
                  <input type="number" value={milestone.reward.amount} onChange={(e) => updateMilestone(index, "amount", parseInt(e.target.value) || 0)} min={0} className="w-20 px-2 py-1.5 rounded bg-[#111117] border border-[#2A2A45] text-sm text-white/85 font-admin text-center" />
                  <button type="button" onClick={() => removeMilestone(index)} className="w-7 h-7 rounded bg-red-500/10 border border-red-500/20 flex items-center justify-center text-red-400 hover:bg-red-500/20 transition-colors"><X className="w-3 h-3" /></button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Settings */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Shield className="w-4 h-4 text-white/25" />
            <h4 className="text-xs font-semibold text-white/50 uppercase tracking-wider font-admin">Settings</h4>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center gap-3 p-3 rounded-lg bg-[#0C0C12] border border-[#2A2A45]">
              <input type="checkbox" checked={form.resetOnMiss} onChange={(e) => updateForm("resetOnMiss", e.target.checked)} className="w-4 h-4 rounded border-[#2A2A45] bg-[#111117] text-[#F37020] focus:ring-[#F37020]/20" />
              <div><p className="text-sm text-white/80 font-admin">Reset on Miss</p><p className="text-[10px] text-white/30 font-admin">Streak resets if missed</p></div>
            </div>
            <FormField label="Grace Period (days)">
              <input type="number" value={form.gracePeriodDays} onChange={(e) => updateForm("gracePeriodDays", parseInt(e.target.value) || 0)} min={0} max={7} className={inputClass} />
            </FormField>
            <div className="flex items-center gap-3 p-3 rounded-lg bg-[#0C0C12] border border-[#2A2A45]">
              <input type="checkbox" checked={form.isActive} onChange={(e) => updateForm("isActive", e.target.checked)} className="w-4 h-4 rounded border-[#2A2A45] bg-[#111117] text-[#F37020] focus:ring-[#F37020]/20" />
              <div><p className="text-sm text-white/80 font-admin">Active</p><p className="text-[10px] text-white/30 font-admin">Users can claim</p></div>
            </div>
          </div>
        </div>

        {/* Date Range */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Clock className="w-4 h-4 text-white/25" />
            <h4 className="text-xs font-semibold text-white/50 uppercase tracking-wider font-admin">Date Range (Optional)</h4>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <FormField label="Start Date"><input type="date" value={form.startDate} onChange={(e) => updateForm("startDate", e.target.value)} className={inputClass} /></FormField>
            <FormField label="End Date"><input type="date" value={form.endDate} onChange={(e) => updateForm("endDate", e.target.value)} className={inputClass} /></FormField>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-[#2A2A45]">
        <button type="button" onClick={onClose} className="px-4 py-2.5 rounded-lg bg-white/[0.04] border border-[#2A2A45] text-sm text-white/70 font-admin hover:bg-white/[0.08] transition-colors">Cancel</button>
        <button type="button" onClick={onSave} disabled={saving} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#F37020] hover:bg-[#F37020]/90 text-white text-sm font-medium font-admin transition-colors disabled:opacity-50">
          {saving ? (<><Loader2 className="w-4 h-4 animate-spin" />Saving...</>) : (<><Check className="w-4 h-4" />{editingId ? "Update" : "Create"}</>)}
        </button>
      </div>
    </motion.div>
  )
}
